// src/lib/taxonomy/types.ts
// Type definitions for the capability taxonomy and inference system

/**
 * A capability that can be proven by evidence
 */
export interface CapabilityProof {
  capability: string;           // Capability ID (e.g., 'cloud_computing')
  confidence: number;           // 0-1 scale, how confident is this inference
  description?: string;         // Human-readable description
}

/**
 * Technology node in the hierarchy
 * Defines what capabilities a technology proves
 */
export interface TechnologyNode {
  id: string;                   // Unique identifier (e.g., 'react')
  name: string;                 // Display name (e.g., 'React')
  aliases: string[];            // Alternative names/spellings
  category: TechnologyCategory;
  proves: CapabilityProof[];    // What capabilities this technology proves
  relatedTechnologies?: string[]; // IDs of related technologies
}

export type TechnologyCategory =
  | 'frontend'
  | 'backend'
  | 'database'
  | 'cloud'
  | 'devops'
  | 'mobile'
  | 'security'
  | 'data'
  | 'ai_ml'
  | 'networking'
  | 'operating_systems'
  | 'project_management'
  | 'other';

/**
 * Certification node
 * Defines what capabilities a certification proves
 */
export interface CertificationNode {
  id: string;                   // Unique identifier
  name: string;                 // Full certification name
  aliases: string[];            // Abbreviations and variations
  issuer: string;               // Issuing organization
  category: CertificationCategory;
  proves: CapabilityProof[];
  level?: string;               // e.g., 'Associate', 'Professional', 'Expert'
  federalRelevance: 'HIGH' | 'MEDIUM' | 'LOW';  // How relevant for federal contracts
}

export type CertificationCategory =
  | 'cloud'
  | 'security'
  | 'project_management'
  | 'it_service_management'
  | 'development'
  | 'data'
  | 'networking'
  | 'clearance'
  | 'business'
  | 'quality'
  | 'industry_specific';

/**
 * Security clearance node
 * Special type of certification for federal work
 */
export interface ClearanceNode {
  id: string;
  name: string;
  aliases: string[];
  level: ClearanceLevel;
  proves: CapabilityProof[];
  accessLevel: string;          // What this clearance allows access to
}

export type ClearanceLevel =
  | 'PUBLIC_TRUST'
  | 'CONFIDENTIAL'
  | 'SECRET'
  | 'TOP_SECRET'
  | 'TS_SCI'
  | 'Q_CLEARANCE'
  | 'L_CLEARANCE';

/**
 * Agency domain node
 * Maps federal agencies to their domains and implied capabilities
 */
export interface AgencyDomainNode {
  id: string;                   // Unique identifier
  name: string;                 // Full agency name
  aliases: string[];            // Abbreviations (e.g., 'VA', 'DoD')
  department?: string;          // Parent department if applicable
  domain: AgencyDomain;
  subDomains?: string[];        // More specific domains
  proves: CapabilityProof[];    // What working with this agency proves
  typicalNaics: string[];       // Common NAICS codes for this agency
  typicalSetAsides?: string[];  // Common set-asides for this agency
}

export type AgencyDomain =
  | 'defense'
  | 'healthcare'
  | 'finance'
  | 'transportation'
  | 'energy'
  | 'environment'
  | 'homeland_security'
  | 'justice'
  | 'agriculture'
  | 'education'
  | 'housing'
  | 'labor'
  | 'veterans'
  | 'general_services'
  | 'intelligence'
  | 'science'
  | 'state'
  | 'treasury'
  | 'commerce';

/**
 * NAICS relationship node
 * Defines relationships between NAICS codes
 */
export interface NaicsRelationship {
  code: string;                 // NAICS code (e.g., '541512')
  title: string;                // Official title
  description: string;          // What this code covers
  category: NaicsCategory;
  parentCode?: string;          // 4-digit parent code
  relatedCodes: string[];       // Related NAICS codes
  proves: CapabilityProof[];    // What having this NAICS proves
  commonKeywords: string[];     // Keywords associated with this NAICS
}

export type NaicsCategory =
  | 'it_services'
  | 'professional_services'
  | 'construction'
  | 'manufacturing'
  | 'healthcare'
  | 'engineering'
  | 'research'
  | 'administrative'
  | 'transportation'
  | 'facilities'
  | 'other';

/**
 * Scale inference rule
 * What capabilities are proven by contracts of certain sizes
 */
export interface ScaleRule {
  id: string;
  minValue: number;             // Minimum contract value threshold
  maxValue?: number;            // Optional maximum (for ranges)
  label: string;                // Human-readable label (e.g., 'Large Federal Contract')
  proves: CapabilityProof[];
  additionalContext?: string;   // Additional context for this scale
}

/**
 * Duration inference rule
 * What capabilities are proven by contract durations
 */
export interface DurationRule {
  id: string;
  minMonths: number;
  maxMonths?: number;
  label: string;
  proves: CapabilityProof[];
}

/**
 * Capability synonym mapping
 * Maps various terms to canonical capability IDs
 */
export interface CapabilitySynonym {
  canonicalId: string;          // The standard capability ID
  canonicalName: string;        // The standard display name
  synonyms: string[];           // Alternative terms that mean the same thing
  category: string;             // Capability category
  description: string;          // What this capability means
}

/**
 * Inference result from the taxonomy system
 */
export interface TaxonomyInference {
  capability: string;           // Capability ID
  capabilityName: string;       // Human-readable name
  confidence: number;           // 0-1 confidence score
  inferenceType: InferenceType;
  sourceType: string;           // What type of evidence this came from
  sourceValue: string;          // The actual value that triggered inference
  inferenceChain: string;       // Human-readable inference path
}

export type InferenceType =
  | 'technology'
  | 'certification'
  | 'clearance'
  | 'agency_domain'
  | 'naics'
  | 'scale'
  | 'duration'
  | 'keyword';

/**
 * Match result when searching for capabilities
 */
export interface TaxonomyMatch {
  matchedTerm: string;          // The term that matched
  matchedIn: string;            // Where it was found (e.g., 'name', 'alias')
  nodeType: 'technology' | 'certification' | 'clearance' | 'agency' | 'naics';
  nodeId: string;               // ID of the matched node
  proves: CapabilityProof[];    // Capabilities this match proves
}

/**
 * Relevance scoring factors
 */
export interface RelevanceFactors {
  naicsMatch: number;           // 0-1: How well NAICS codes match
  agencyMatch: number;          // 0-1: How well agency matches
  scopeMatch: number;           // 0-1: How well scope/keywords match
  recencyScore: number;         // 0-1: How recent is the evidence
  valueMatch: number;           // 0-1: How well contract values match
  setAsideMatch: number;        // 0-1: How well set-asides match
}

export interface RelevanceWeights {
  naics: number;
  agency: number;
  scope: number;
  recency: number;
  value: number;
  setAside: number;
}

export const DEFAULT_RELEVANCE_WEIGHTS: RelevanceWeights = {
  naics: 0.25,
  agency: 0.20,
  scope: 0.20,
  recency: 0.15,
  value: 0.10,
  setAside: 0.10
};

/**
 * Relevance tier thresholds
 */
export type RelevanceTier = 'HIGH' | 'MEDIUM' | 'LOW' | 'MINIMAL';

export const RELEVANCE_TIER_THRESHOLDS: Record<RelevanceTier, number> = {
  HIGH: 0.7,
  MEDIUM: 0.5,
  LOW: 0.3,
  MINIMAL: 0
};

/**
 * Helper type for taxonomy lookup results
 */
export interface TaxonomyLookupResult<T> {
  found: boolean;
  node?: T;
  matchedBy: 'id' | 'name' | 'alias' | 'keyword' | null;
  matchedTerm?: string;
}
